import React from 'react'

import { validateUniqueName } from '@/views/Leaderboards/helpers'
import { qaRankIdentifiers, QA_CRITERION_RANK_IDENTIFIER } from '@/utils/constants'
import { LeaderboardQADetail } from '@/views/Leaderboards/LeaderboardForm/LeaderboardQADetail'

export const getSchema = ({
  isQA,
  isQACriterion,
  criteriaOptions,
  determineIfQASelected,
  handleScorecardSelect,
  scorecardValues,
  playbookOptions,
  scorecardConfigOptions,
  rankOnOptions,
  timePeriodOptions,
  tagsOptionsWithCategories,
  leaderboardOptions,
}) => {
  const schema = [
    {
      label: 'Leaderboard Name',
      placeholder: 'Name your leaderboard',
      name: 'name',
      type: 'text',
      required: true,
      validate: (value) => validateUniqueName(value, leaderboardOptions),
    },
    {
      label: 'Rank On',
      name: 'rank_identifier',
      type: 'select',
      options: rankOnOptions,
      defaultValue: 'win_rate',
      required: true,
      onChange: (value = {}, formValues = {}) => determineIfQASelected({ ...value, ...formValues }),
    },
    {
      label: 'Playbook',
      name: 'config_ids',
      type: 'multiSelect',
      options: playbookOptions,
      condenseOptions: false,
      dependencies: [
        {
          key: 'rank_identifier',
          operator: 'notIn',
          value: qaRankIdentifiers,
        },
      ],
    },
    {
      label: 'QA Scorecard',
      name: 'scorecard_config_sid',
      type: 'select',
      options: scorecardConfigOptions,
      onChange: (value) => handleScorecardSelect(value || {}),
      condenseOptions: false,
      required: isQA,
      dependencies: [
        {
          key: 'rank_identifier',
          operator: 'in',
          value: qaRankIdentifiers,
        },
      ],
    },
    {
      label: 'QA Criterion',
      name: 'scorecard_criteria_scid',
      type: 'select',
      options: criteriaOptions,
      condenseOptions: false,
      required: isQACriterion,
      labelTooltip:
        'Agents are ranked by how often they pass this one criterion. ' +
        'Calls where it was marked N/A are left out.',
      // Switching scorecards leaves the old scid in the form: the field stays
      // visible, so AbstractForm's reset-on-hide never runs, and `required`
      // passes on a value that belongs to the previous scorecard. Saving that
      // pair returns an empty board, so fail validation instead.
      validate: (value) =>
        value && !criteriaOptions.some((option) => option.value === value)
          ? 'Pick a criterion from the selected scorecard'
          : undefined,
      dependencies: [
        {
          key: 'rank_identifier',
          operator: '===',
          value: QA_CRITERION_RANK_IDENTIFIER,
        },
        {
          key: 'scorecard_config_sid',
        },
      ],
    },
    {
      raw: true,
      as: <LeaderboardQADetail scorecard={scorecardValues} />,
      dependencies: [
        {
          key: 'rank_identifier',
          operator: 'in',
          value: qaRankIdentifiers,
        },
        {
          key: 'scorecard_config_sid',
        },
      ],
    },
    {
      label: 'Visible to Agents',
      name: 'agent_visible',
      type: 'checkbox',
    },
    {
      label: 'Include Managers',
      name: 'include_managers',
      type: 'checkbox',
    },
    {
      label: 'Time Period',
      name: 'date_range',
      type: 'select',
      options: timePeriodOptions,
      defaultValue: 'today',
      required: true,
    },
    {
      label: 'Tag Filters',
      name: 'tags',
      type: 'multiSelect',
      options: tagsOptionsWithCategories,
      condenseOptions: false,
    },
    {
      label: 'Call Duration Minimum',
      name: 'call_duration',
      type: 'text',
      inputType: 'number',
      placeholder: 0,
      dependencies: [
        {
          key: 'rank_identifier',
          operator: '!==',
          value: 'qa',
        },
      ],
    },
  ]

  return schema
}
